// frontend/src/Hooks/useHandleRedirect.js
// Minimal redirect hook: navigate inside the app with react-router,
// optionally update Redux route state, and use full redirects only
// for external or special links (mailto/tel).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useHandleRedirect
 * @param {Object} options
 * @param {(path: string) => void} [options.onReduxRedirect] - optional callback to update route state (e.g. dispatch(authActions.redirectScreen(path)))
 * @returns {(evtOrPath: MouseEvent | string, explicitPath?: string) => void}
 */
export default function useHandleRedirect(options = {}) {
  const { onReduxRedirect } = options;
  const navigate = useNavigate();

  return React.useCallback((evtOrPath, explicitPath) => {
    // Resolve path from either a string or an event/element
    let path = '';
    if (typeof evtOrPath === 'string') {
      path = evtOrPath;
    } else {
      evtOrPath?.preventDefault?.();
      path =
        explicitPath ||
        evtOrPath?.currentTarget?.value ||
        evtOrPath?.currentTarget?.getAttribute?.('href') ||
        '/landing';
    }

    const isExternal = /^https?:\/\//i.test(path);
    const isSpecial = /^(mailto:|tel:)/i.test(path);

    // External or special links -> let the browser handle a full redirect
    // console.log('[useHandleRedirect] Navigating to:', path);

    if (isExternal || isSpecial) {
      window.location.assign(path);
      return;
    }

    // Optional route-state update (Redux, etc.) supplied by caller
    if (typeof onReduxRedirect === 'function') {
      try {
        onReduxRedirect(path);
      } catch (_) {
        // no-op
      }
    }

    // Simple SPA navigation
    navigate(path);
  }, [navigate, onReduxRedirect]);
}